import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import {
  Button,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  PopoverBody,
  Row,
  UncontrolledPopover,
} from 'reactstrap';
import { ProductCategoryModel, ProductModel } from '../../../../shared/orders';
import { InlineHelp, MaterialIcon, PlatformFeeHelp, RadioButtonGroup } from '../../../components';
import { NullableNumberInput } from '../../../components/NullableNumberInput';
import { ProductSelection } from '../../../components/ProductSelection';
import { RoleSelection } from '../../../components/RoleSelection';
import {
  migrateTimezone,
  NullableTimeZoneInputField,
  TimeZoneInputField,
  trimTime,
} from '../../../components/TimeZoneInputField';
import { JSONForm, PreSubmitData } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { useConfig, useConvention } from '../../../utils';
import { checkForPlatformFee } from './utils';

interface ProductFormProps {
  readonly method: 'patch' | 'post';
  readonly action: string;
  readonly product?: ProductModel;
  readonly category: ProductCategoryModel;
  readonly onSuccess: (props: { id?: number }) => void;
}

interface ProductFormForm {
  stockMaximumQuantity: number | null;
  price: number | null;
  roleRestriction: number[] | null;
  roleBlacklist: number[] | null;
  grantRestriction: number[] | null;
}

// eslint-disable-next-line max-lines-per-function
export const ProductForm: FC<ProductFormProps> = ({
  action,
  method,
  category,
  product,
  onSuccess,
}) => {
  const { ts } = useTranslation();
  const { currencySymbol } = useConfig();
  const { timeZone } = useConvention();
  const [maxStock, setMaxStock] = useState<number | null>(
    product ? product.stockMaximumQuantity! : null,
  );

  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>(product?.roleRestriction ?? []);
  const [roleBlacklist, setRoleBlacklist] = useState<number[]>(product?.roleBlacklist ?? []);
  const [selectedGrantIds, setSelectedGrantIds] = useState<number[]>(
    (product?.requiredGrants ?? []).map((grant) => grant.productId),
  );

  const [availableTo, setAvailableTo] = useState(() => product?.availableTo ?? null);
  const [availableFrom, setAvailableFrom] = useState<Date>(
    () => product?.availableFrom ?? migrateTimezone(trimTime(new Date()), timeZone),
  );

  const [expiresOn, setExpiresOn] = useState(() => product?.grantExpiresOn ?? null);

  const toggleMaxStock = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setMaxStock(checked ? (product?.stockMaximumQuantity ?? 1) : null);
    },
    [setMaxStock, product],
  );

  const preSubmit = useCallback(
    ({ inputs }: PreSubmitData<ProductFormForm>) => {
      inputs.roleRestriction = selectedRoleIds.length === 0 ? null : selectedRoleIds;
      inputs.roleBlacklist = roleBlacklist.length === 0 ? null : roleBlacklist;
      inputs.grantRestriction = selectedGrantIds;
      inputs.stockMaximumQuantity = maxStock;
      if (inputs.price) {
        inputs.price = Math.floor(inputs.price * 100);
      }
    },
    [maxStock, selectedRoleIds, selectedGrantIds, roleBlacklist],
  );

  return (
    <JSONForm<{ id?: number }, ProductFormForm>
      method={method}
      onSuccess={onSuccess}
      path={action}
      preSubmit={preSubmit}
    >
      <Row>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="name">{ts('name')}</Label>
            <Input
              defaultValue={product?.name}
              id="name"
              name="name"
              placeholder={ts('common.product_name')}
              required
            />
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="displayName">
              {ts('common.display_name')} <small>{ts('common.optional')}</small>
            </Label>
            <Input defaultValue={product?.displayName ?? ''} id="displayName" name="displayName" />
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label for="description">{ts('description')}</Label>
        <Input
          defaultValue={product?.description}
          id="description"
          name="description"
          type="textarea"
        />
      </FormGroup>
      <Row>
        {!product && (
          <>
            <Col lg={6} xs={12}>
              <FormGroup>
                <Label for="price">{ts('base_price', currencySymbol)}</Label>
                <Input id="price" min="0" name="price" required step="0.01" type="number" />
              </FormGroup>
            </Col>
            <Col lg={6} xs={12} />
          </>
        )}
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="stockUnlimited">{ts('stock_control')}</Label>
            <FormGroup check className="mt-5">
              <Input
                defaultChecked={maxStock !== null}
                id="stockUnlimited"
                onChange={toggleMaxStock}
                type="checkbox"
              />
              <Label check for="stockUnlimited">
                {ts('limit_available_stock')}
              </Label>
            </FormGroup>
            <FormText className="mt-10" color="muted">
              {ts('limiting_the_stock_will_allow')}
            </FormText>
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          {maxStock !== null && (
            <FormGroup>
              <Label for="stockMaximumQuantity">{ts('max_stock')}</Label>
              <Input
                defaultValue={maxStock.toString()}
                id="stockMaximumQuantity"
                min="1"
                onChange={(event) => {
                  setMaxStock(Number(event.target.value));
                }}
                required
                step="1"
                type="number"
              />
              <FormText color="muted">{ts('maximum_stock_can_only_be')}</FormText>
            </FormGroup>
          )}
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="availableFrom">{ts('common.available_from')}</Label>
            <Input name="availableFrom" type="hidden" value={availableFrom.toISOString()} />
            <TimeZoneInputField
              defaultValue={availableFrom}
              id="availableFrom"
              onChange={setAvailableFrom}
              timeZone={timeZone}
            />
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="availableTo">{ts('common.available_until')}</Label>
            <Input
              name="availableTo"
              type="hidden"
              value={availableTo ? availableTo.toISOString() : ''}
            />
            <NullableTimeZoneInputField
              defaultValue={availableTo}
              id="availableTo"
              min={availableFrom}
              onChange={setAvailableTo}
              timeZone={timeZone}
            />
          </FormGroup>
        </Col>
        {category.isRegistration && (
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="grantExpiresOn">{ts('expires_on')}</Label>{' '}
              {expiresOn && expiresOn.toISOString() !== availableTo?.toISOString() && (
                <>
                  <span className="inline-icon text-danger" id="expiresWarning">
                    <MaterialIcon name="warning" small />
                  </span>
                  <UncontrolledPopover placement="bottom" target="expiresWarning" trigger="hover">
                    <PopoverBody>
                      {ts(
                        'expires_on_does_not_match_available_until_double_check_that_this_is_intentional',
                      )}
                    </PopoverBody>
                  </UncontrolledPopover>
                </>
              )}
              <Input
                name="grantExpiresOn"
                type="hidden"
                value={expiresOn ? expiresOn.toISOString() : ''}
              />
              <NullableTimeZoneInputField
                defaultValue={expiresOn}
                id="grantExpiresOn"
                onChange={setExpiresOn}
                timeZone={timeZone}
              />
            </FormGroup>
          </Col>
        )}
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="minAge">
              {ts('minimum_purchase_age')} <small>{ts('common.optional')}</small>
            </Label>
            <Input
              defaultValue={product?.minAge}
              id="minAge"
              max={product?.maxAge ?? 150}
              min={0}
              name="minAge"
              type="number"
            />
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="maxAge">
              {ts('maximum_purchase_age')} <small>{ts('common.optional')}</small>
            </Label>
            <Input
              defaultValue={product?.maxAge}
              id="maxAge"
              max={150}
              min={product?.minAge ?? 0}
              name="maxAge"
              type="number"
            />
          </FormGroup>
        </Col>
        {!category.isVendor && (
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="grantRestriction">
                {ts('restrict_product_to_grants')}
                <InlineHelp>{ts('the_user_will_not_be')}</InlineHelp>
              </Label>
              <ProductSelection
                excludedIds={product ? [product.id] : []}
                permissionError={ts('you_do_not_have_sufficient')}
                selectedIds={selectedGrantIds}
                setSelectedIds={setSelectedGrantIds}
              />
            </FormGroup>
          </Col>
        )}
        {!category.isRegistration && !category.isVendor && (
          <Col lg={6} xs={12}>
            <FormGroup>
              <NullableNumberInput
                defaultValue={product?.maxGrants ?? null}
                id="maxGrants"
                name="maxGrants"
                step={1}
              >
                {ts('maximum_purchases_per_user')}
              </NullableNumberInput>
            </FormGroup>
          </Col>
        )}
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="roleRestriction">
              {ts('product.settings.whitelist')}
              <InlineHelp>
                <Label>{ts('product.settings.whitelist')}</Label>
                <ul>
                  <li>{ts('product.settings.list_help_a')}</li>
                  <li>{ts('product.settings.list_help_b')}</li>
                  <li>{ts('product.settings.whitelist_help')}</li>
                </ul>
                <p>{ts('product.settings.whitelist_help_tip')}</p>
              </InlineHelp>
            </Label>

            <RoleSelection
              disable={roleBlacklist.length > 0}
              idSuffix="Whitelist"
              permissionError={ts('you_do_not_have_sufficient_products_by_role')}
              selectedIds={selectedRoleIds}
              setSelectedIds={setSelectedRoleIds}
            />

            {roleBlacklist.length > 0 && (
              <FormText color="muted">{ts('product.settings.whitelist_disable')}</FormText>
            )}
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="roleBlacklist">
              {ts('product.settings.product_blacklist')}
              <InlineHelp>
                <Label>{ts('product.settings.product_blacklist')}</Label>
                <ul>
                  <li>{ts('product.settings.list_help_a')}</li>
                  <li>{ts('product.settings.list_help_b')}</li>
                  <li>{ts('product.settings.blacklist_help')}</li>
                </ul>
                <p>{ts('product.settings.blacklist_help_tip')}</p>
              </InlineHelp>
            </Label>
            <RoleSelection
              disable={selectedRoleIds.length > 0}
              idSuffix="Blacklist"
              permissionError={ts('you_do_not_have_sufficient_products_by_role')}
              selectedIds={roleBlacklist}
              setSelectedIds={setRoleBlacklist}
            />
            {selectedRoleIds.length > 0 && (
              <FormText color="muted">{ts('product.settings.blacklist_disable')}</FormText>
            )}
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup>
            <Label for="visibility">{ts('product_visibility')}</Label>
            <RadioButtonGroup
              defaultSelected={product?.visibility}
              id="visibility"
              name="visibility"
              options={[
                { label: 'public', value: 'public', icon: 'public' },
                { label: 'unlisted', value: 'unlisted', icon: 'link' },
                { label: 'hidden', value: 'hidden', icon: 'lock' },
              ]}
            />
          </FormGroup>
        </Col>
        <Col lg={6} xs={12}>
          <FormGroup check className="mt-10">
            <Input
              defaultChecked={product && checkForPlatformFee(product)}
              id="passPlatformFee"
              name="passPlatformFee"
              type="checkbox"
            />
            <Label check for="passPlatformFee">
              {ts('pass_platform_fee_to_customer')}
              <PlatformFeeHelp />
            </Label>
          </FormGroup>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col lg={8} xs={12}>
          <Button block color="primary" id="submitNewProduct">
            {ts(product ? 'update_product' : 'create_product')}
          </Button>
        </Col>
      </Row>
    </JSONForm>
  );
};
